/**
 * Geometry + forecast message dispatcher (ticket 054).
 *
 * Routes live update pushes and on-subscribe snapshots to `geometryStore`.
 * Returns true when the message was handled so `messageDispatcher` can stop
 * the chain.
 *
 * Server pushes `msg.symbol` as the wsSymbol (e.g. "BTC/USD"), but overlay
 * components read store entries keyed by the URL/raw symbol code
 * (e.g. "XXBTZUSD"). Normalize via the symbol store before writing, the
 * same pattern chart/market dispatchers use.
 */

import type { ServerToClientMessage } from '../types';
import { useGeometryStore } from '../../stores/geometryStore';
import { useSymbolStore, normalizeServerSymbol } from '../../stores/symbolStore';

function normalize(symbol: string): string {
  return normalizeServerSymbol(symbol, useSymbolStore.getState().wsToCode);
}

export function dispatchGeometryMessage(msg: ServerToClientMessage): boolean {
  switch (msg.type) {
    case 'swing-point': {
      useGeometryStore.getState().addSwingPoint(normalize(msg.symbol), msg.interval, msg.data);
      return true;
    }
    case 'trendline': {
      useGeometryStore.getState().upsertTrendline(normalize(msg.symbol), msg.interval, msg.data);
      return true;
    }
    case 'pattern': {
      useGeometryStore.getState().upsertPattern(normalize(msg.symbol), msg.interval, msg.data);
      return true;
    }
    case 'trend-state': {
      useGeometryStore.getState().setTrendState(normalize(msg.symbol), msg.interval, msg.data);
      return true;
    }
    case 'forecast': {
      useGeometryStore.getState().setForecast(normalize(msg.symbol), msg.interval, msg.data);
      return true;
    }
    case 'trend-state-snapshot': {
      if (msg.data) {
        useGeometryStore.getState().setTrendState(normalize(msg.symbol), msg.interval, msg.data);
      }
      return true;
    }
    case 'forecast-snapshot': {
      const symbol = normalize(msg.symbol);
      const store = useGeometryStore.getState();
      if (msg.deterministic) store.setForecast(symbol, msg.interval, msg.deterministic);
      if (msg.ml) store.setForecast(symbol, msg.interval, msg.ml);
      return true;
    }
    case 'swing-points-snapshot': {
      const symbol = normalize(msg.symbol);
      const store = useGeometryStore.getState();
      for (const pivot of msg.pivots) {
        store.addSwingPoint(symbol, msg.interval, pivot);
      }
      return true;
    }
    case 'trendlines-snapshot': {
      const symbol = normalize(msg.symbol);
      const store = useGeometryStore.getState();
      for (const line of msg.lines) {
        store.upsertTrendline(symbol, msg.interval, line);
      }
      return true;
    }
    case 'patterns-snapshot': {
      const symbol = normalize(msg.symbol);
      const store = useGeometryStore.getState();
      for (const pattern of msg.patterns) {
        store.upsertPattern(symbol, msg.interval, pattern);
      }
      return true;
    }
    default:
      return false;
  }
}
